import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MESSAGE_PRIORITY } from 'src/constants/messagePriority';
import useAuth from 'src/hooks/useAuth';
import useCompany from 'src/hooks/useCompany';
import {
  MessageTreeNode,
  useMessageTreeApi,
} from 'src/services/messages_services/useMessageTreeApi';

export interface ComposeFormData {
  recipient: string;
  subject: string;
  message: string;
  priority: string;
  attachments: File[];
}

export interface ComposeMessageFormProps {
  formData: ComposeFormData;
  selectedRecipients: string[];
  errorState: { hasError: boolean; message: string };
  replyToMessage?: {
    messageId: string;
    subject: string;
    sender: string;
  };
  clientId?: string;
  onInputChange: (field: string, value: string) => void;
  onRecipientChange: (recipientId: string, isChecked: boolean) => void;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveAttachment: (index: number) => void;
}

const ComposeMessageForm: FC<ComposeMessageFormProps> = ({
  formData,
  selectedRecipients,
  errorState,
  replyToMessage,
  clientId,
  onInputChange,
  onRecipientChange,
  onFileUpload,
  onRemoveAttachment,
}) => {
  const { t } = useTranslation();
  const { company } = useCompany();
  const { clientId: tokenClientId, companyId: tokenCompanyId } = useAuth();
  const [isTreeExpanded, setIsTreeExpanded] = useState(false);

  // Get company's clientId for fetching message tree
  const companyClientId = useMemo(() => {
    // Step 1: Try to get companyId from JWT token
    const userCompanyId = tokenCompanyId || company?.companyId;

    if (!userCompanyId && !tokenClientId) return '';

    return tokenClientId || '';
  }, [tokenClientId, tokenCompanyId, company?.companyId]);

  const { tree, loading, error, refresh } = useMessageTreeApi(
    tokenCompanyId || '',
    tokenClientId || ''
  );

  // Helper to collect all clientIds from the tree recursively
  const collectAllClientIds = useCallback((nodes: MessageTreeNode[]): string[] => {
    const ids: string[] = [];
    for (const node of nodes) {
      ids.push(node.clientId || node.id);
      if (node.children && node.children.length > 0) {
        ids.push(...collectAllClientIds(node.children));
      }
    }
    return ids;
  }, []);

  // Get all clientIds in the tree
  const allClientIds = useMemo(() => {
    return tree ? collectAllClientIds(tree) : [];
  }, [tree, collectAllClientIds]);

  // Check if all recipients are selected
  const areAllSelected = useMemo(() => {
    if (allClientIds.length === 0) return false;
    return allClientIds.every((id) => selectedRecipients.includes(id));
  }, [allClientIds, selectedRecipients]);

  // Handler for select all checkbox
  const handleSelectAll = useCallback(
    (isChecked: boolean) => {
      if (errorState.hasError) return;

      // Call onRecipientChange for all clientIds with the same checked state
      allClientIds.forEach((id) => {
        onRecipientChange(id, isChecked);
      });
    },
    [allClientIds, onRecipientChange, errorState.hasError]
  );

  // Helper to count total nodes in the tree (recursive)
  const countTreeNodes = useCallback((nodes: MessageTreeNode[]): number => {
    let count = 0;
    for (const node of nodes) {
      count += 1; // Count this node
      if (node.children && node.children.length > 0) {
        count += countTreeNodes(node.children); // Count children recursively
      }
    }
    return count;
  }, []);

  // Calculate total node count
  const totalNodeCount = useMemo(() => {
    return tree ? countTreeNodes(tree) : 0;
  }, [tree, countTreeNodes]);

  // Show expander only if there are 5 or more items
  const showExpander = totalNodeCount >= 5;

  // Check if there's only one recipient
  const singleRecipient = useMemo(() => {
    if (tree && tree.length === 1 && (!tree[0].children || tree[0].children.length === 0)) {
      return tree[0];
    }
    return null;
  }, [tree]);

  // Auto-select single recipient when it's detected
  useEffect(() => {
    if (
      singleRecipient &&
      !selectedRecipients.includes(singleRecipient.clientId || singleRecipient.id)
    ) {
      onRecipientChange(singleRecipient.clientId || singleRecipient.id, true);
    }
  }, [singleRecipient, selectedRecipients, onRecipientChange]);

  // Helper to render tree recursively, sorting each branch alphabetically by label
  const renderTree = useCallback(
    (nodes: MessageTreeNode[], isRoot = true, parentPath: string = '') => {
      // Sort nodes alphabetically by label (case-insensitive)
      const sortedNodes = [...nodes].sort((a, b) => {
        const labelA = a.label
          ? a.label
              .replace(/\s*\([^)]*\)/g, '')
              .trim()
              .toLowerCase()
          : '';
        const labelB = b.label
          ? b.label
              .replace(/\s*\([^)]*\)/g, '')
              .trim()
              .toLowerCase()
          : '';
        return labelA.localeCompare(labelB);
      });
      return (
        <ul className="pl-2">
          {sortedNodes.map((node, idx) => {
            const nodePath = parentPath ? `${parentPath}-${idx}` : `${idx}`;
            const uniqueId = `${node.id}-${nodePath}`;
            const clientIdValue = node.clientId || node.id;
            // Extract main label without any parenthetical content
            const mainLabel =
              node.label && node.label.trim()
                ? node.label.replace(/\s*\([^)]*\)/g, '').trim()
                : `[${node.id}]`;
            return (
              <li key={uniqueId} className="mb-1">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={uniqueId}
                    value={clientIdValue}
                    className="form-checkbox h-4 w-4 text-blue-600"
                    checked={selectedRecipients.includes(clientIdValue)}
                    onChange={(e) => onRecipientChange(clientIdValue, e.target.checked)}
                    disabled={errorState.hasError}
                  />
                  <label
                    htmlFor={uniqueId}
                    className={`text-xs font-medium text-gray-900 cursor-pointer${
                      isRoot && idx === 0 ? ' font-bold' : ''
                    }`}
                  >
                    {mainLabel}
                  </label>
                </div>
                {node.children && node.children.length > 0 && (
                  <div className="ml-6 mt-1">{renderTree(node.children, false, nodePath)}</div>
                )}
              </li>
            );
          })}
        </ul>
      );
    },
    [selectedRecipients, onRecipientChange, errorState.hasError]
  );

  return (
    <div className={`${errorState.hasError ? 'opacity-60' : ''}`} style={{ overflowX: 'hidden' }}>
      {singleRecipient ? (
        // Single column layout when there's only one recipient
        <div className="space-y-4">
          {/* To Field - Single Recipient */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <label className="message-label">
                {t('To')} <span className="text-red-500">*</span>
              </label>
            </div>
            <div
              className="border border-gray-300 rounded-md p-3 bg-gray-50"
              style={{ width: '100%', maxWidth: '700px' }}
            >
              <div className="text-sm font-medium text-gray-900">
                {singleRecipient.label
                  ? singleRecipient.label.replace(/\s*\([^)]*\)/g, '').trim()
                  : `[${singleRecipient.id}]`}
              </div>
            </div>
          </div>

          {/* Priority */}
          <div>
            <label className="message-label">{t('Priority')}</label>
            <select
              value={formData.priority}
              onChange={(e) => onInputChange('priority', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              style={{ width: '367px' }}
              disabled={errorState.hasError}
            >
              {Object.entries(MESSAGE_PRIORITY).map(([key, value]) => (
                <option key={value} value={value}>
                  {t(value)}
                </option>
              ))}
            </select>
          </div>

          {/* Subject */}
          <div>
            <label className="message-label">
              {t('Subject')} <span className="text-red-500">*</span>
              <span className="ml-2 italic text-gray-400 text-xs align-middle">
                {t('(limit 255 characters)')}
              </span>
              {formData.subject.length >= 255 && (
                <span className="ml-2 italic text-red-500 text-xs align-middle font-semibold">
                  {t('Exceeded limit')}
                </span>
              )}
            </label>
            <textarea
              value={formData.subject}
              onChange={(e) => onInputChange('subject', e.target.value)}
              placeholder={t('Enter message subject')}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none overflow-y-auto"
              style={{ width: '100%', maxWidth: '665px', height: '50px' }}
              maxLength={255}
              autoComplete="off"
              readOnly={errorState.hasError}
            />
          </div>

          {/* Message */}
          <div>
            <label className="message-label">
              {t('Message')} <span className="text-red-500">*</span>
              <span className="ml-2 italic text-gray-400 text-xs align-middle">
                {t('(limit 4,000 characters)')}
              </span>
              {formData.message.length >= 4000 && (
                <span className="ml-2 italic text-red-500 text-xs align-middle font-semibold">
                  {t('Exceeded limit')}
                </span>
              )}
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => onInputChange('message', e.target.value)}
              placeholder={t('Type your message here...')}
              className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none overflow-y-auto"
              style={{ width: '100%', maxWidth: '665px', height: '320px', minHeight: '200px' }}
              maxLength={4000}
              autoComplete="off"
              readOnly={errorState.hasError}
            />
          </div>
        </div>
      ) : (
        // Two-column layout on desktop, stacks vertically on mobile
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-[20px]">
          {/* Left Column - Recipient "To" Tree */}
          <div>
            <div className="flex items-center gap-2">
              <label className="message-label">
                {t('To')} <span className="text-red-500">*</span>
              </label>
            </div>
            <div
              className="border border-gray-300 rounded-md bg-white"
              style={{
                width: '100%',
                maxWidth: '415px',
                height: '310px',
                maxHeight: '310px',
                overflowY: 'auto',
                overflowX: 'auto',
              }}
            >
              {/* Select All Checkbox */}
              {!loading && !error && tree && tree.length > 0 && allClientIds.length > 0 && (
                <div
                  className="px-3 py-2 flex items-center space-x-2 sticky top-0 z-10"
                  style={{ backgroundColor: '#2596be' }}
                >
                  <input
                    type="checkbox"
                    id="select-all-recipients"
                    className="form-checkbox h-4 w-4 text-white border-white"
                    style={{ backgroundColor: '#2596be' }}
                    checked={areAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    disabled={errorState.hasError}
                  />
                  <label
                    htmlFor="select-all-recipients"
                    className="text-sm font-medium text-white cursor-pointer select-none"
                  >
                    {t('Select All')}
                  </label>
                </div>
              )}

              <div className="p-3">
                {loading && <div className="text-sm text-gray-500">{t('Loading tree...')}</div>}
                {error && (
                  <div className="text-sm text-red-500">
                    {t('Error loading tree')}: {error}
                  </div>
                )}
                {!loading && !error && tree && tree.length > 0 && renderTree(tree, true)}
                {!loading && !error && (!tree || tree.length === 0) && (
                  <div className="text-sm text-gray-500">{t('No recipients available.')}</div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Priority, Subject, Message */}
          <div className="flex flex-col space-y-4">
            {/* Priority */}
            <div>
              <label className="message-label">{t('Priority')}</label>
              <select
                value={formData.priority}
                onChange={(e) => onInputChange('priority', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={{ width: '167px' }}
                disabled={errorState.hasError}
              >
                {Object.entries(MESSAGE_PRIORITY).map(([key, value]) => (
                  <option key={value} value={value}>
                    {t(value)}
                  </option>
                ))}
              </select>
            </div>

            {/* Subject */}
            <div>
              <label className="message-label">
                {t('Subject')} <span className="text-red-500">*</span>
                <span className="ml-2 italic text-gray-400 text-xs align-middle">
                  {t('(limit 255 characters)')}
                </span>
                {formData.subject.length >= 255 && (
                  <span className="ml-2 italic text-red-500 text-xs align-middle font-semibold">
                    {t('Exceeded limit')}
                  </span>
                )}
              </label>
              <textarea
                value={formData.subject}
                onChange={(e) => onInputChange('subject', e.target.value)}
                placeholder={t('Enter message subject')}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none overflow-y-auto"
                style={{ width: '100%', maxWidth: '365px', height: '50px' }}
                maxLength={255}
                autoComplete="off"
                readOnly={errorState.hasError}
              />
            </div>

            {/* Message */}
            <div className="flex-1">
              <label className="message-label">
                {t('Message')} <span className="text-red-500">*</span>
                <span className="ml-2 italic text-gray-400 text-xs align-middle">
                  {t('(limit 4,000 characters)')}
                </span>
                {formData.message.length >= 4000 && (
                  <span className="ml-2 italic text-red-500 text-xs align-middle font-semibold">
                    {t('Exceeded limit')}
                  </span>
                )}
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => onInputChange('message', e.target.value)}
                placeholder={t('Type your message here...')}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none overflow-y-auto"
                style={{
                  width: '100%',
                  maxWidth: '365px',
                  height: '120px',
                  minHeight: '150px',
                }}
                maxLength={4000}
                autoComplete="off"
                readOnly={errorState.hasError}
              />
            </div>
          </div>
        </div>
      )}

      {/* Reply Info (if replying to a message) - Below the two-column layout */}
      {replyToMessage && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-md mt-6">
          <div className="text-sm">
            <span className="message-span-label">{t('Replying to')}:</span>
            <div className="text-gray-900">
              <strong>{replyToMessage.sender}</strong> - {replyToMessage.subject}
            </div>
          </div>
        </div>
      )}

      {/* Attachments */}
      <div className="hidden">
        <label className="message-label">{t('Attachments')}</label>
        <div className="space-y-2">
          <input
            type="file"
            multiple
            onChange={onFileUpload}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
            disabled={errorState.hasError}
          />

          {/* Display selected files */}
          {formData.attachments.length > 0 && (
            <div className="space-y-1">
              {formData.attachments.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded"
                >
                  <span className="text-sm text-gray-700">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => onRemoveAttachment(index)}
                    className="text-red-500 hover:text-red-700 text-sm"
                    disabled={errorState.hasError}
                  >
                    {t('Remove')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComposeMessageForm;
